import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo } from 'react';
import { getEvidenceDetails } from '../api/evidences';
import { useGroupedAggregations } from './ideaPage/useIdeaQueries';
import type { EnrichedEvidence, UseEvidenceDataOptions } from '../components/explore/discovery-evidence/types';
import { selectFirstAggregationRow } from '../utils/evidence/aggregationResponse';

export interface UseEvidenceDataReturn {
  evidence: EnrichedEvidence | null;
  isLoading: boolean;
  error: Error | null;
  aggregations: Record<string, unknown> | null;
  refetch: () => void;
}

export function useEvidenceData(
  evidenceId: string | undefined,
  options?: UseEvidenceDataOptions & { refetchDespiteInitial?: boolean }
): UseEvidenceDataReturn {
  const auth0 = useAuth0();
  const initialEvidence = options?.initialEvidence ?? null;

  const shouldFetch =
    !!evidenceId &&
    options?.enabled !== false &&
    (initialEvidence == null || Boolean(options?.refetchDespiteInitial));

  const query = useQuery({
    queryKey: ['evidence-detail', evidenceId],
    queryFn: async () => {
      if (!evidenceId) return null;
      const data = await getEvidenceDetails(evidenceId, auth0);
      return (data ?? null) as EnrichedEvidence | null;
    },
    enabled: shouldFetch,
    placeholderData: initialEvidence ?? undefined,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const evidenceForAgg = (query.data ?? initialEvidence) as EnrichedEvidence | null;
  const ids = evidenceForAgg?._id ? [String(evidenceForAgg._id)] : [];

  const aggQuery = useGroupedAggregations(ids);

  const aggregations = useMemo(
    () => selectFirstAggregationRow(aggQuery.data ?? null),
    [aggQuery.data]
  );

  const evidence = (query.data ?? initialEvidence) as EnrichedEvidence | null;

  return {
    evidence,
    isLoading: query.isLoading && !initialEvidence && shouldFetch,
    error: (query.error as Error) ?? null,
    aggregations,
    refetch: query.refetch,
  };
}
